import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { baseUrl, getDateWithDay } from '../../../comman/config';
import Loader from '../../../components/Loader';
import { useNavigate, useLocation  } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from "jspdf";
import "jspdf-autotable";
import gujaratiFont from '../../../resources/NotoSansGujarati';
import { Link } from 'react-router-dom';
import { ItemNameStacked } from '../../../components/ItemName';
import { menuAboveTable } from "../../../comman/selectMenu";

const MenuCreationEdit = () => {
    const [loading, setLoading] = useState(false);
    const [eventList, setEventList] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [mealData, setMealData] = useState([]);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const defaultSessions = ["breakfast", "lunch", "dinner"];

    useEffect(() => {
        getEventList();
    }, []);

    // Effect to handle auto-selection from state
    useEffect(() => {
        // Check if we have a selected event ID from navigation state
        if (location.state?.selectedEventId && eventList.length > 0) {
            const targetEventId = location.state?.selectedEventId;
            const eventToSelect = eventList.find(event => event.id === targetEventId);
            
            if (eventToSelect) {
                const selectedOption = {
                    value: eventToSelect.id,
                    label: eventToSelect.event_name
                };
                console.log("selected option:",selectedEvent);
                setSelectedEvent(selectedOption);
                fetchMealData(targetEventId);
            }
        }
    }, [eventList, location.state]);

    const getEventList = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${baseUrl}/comman/list?table=event_master&condition=status=1`);
            const data = response.data.data;
            setEventList(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching event list:', error);
            setError('Failed to load events');
            setLoading(false);
        }
    };

    const fetchMealData = async (eventId) => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${baseUrl}/menu_creation/view?event_id=${eventId}`);

            if (response.data.status && response.data.data) {
                // Data comes pre-organized from the backend now
                setMealData(response.data.data);
            } else {
                setMealData([]);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching meal data:', error);
            setError('Failed to load meal data');
            setLoading(false);
        }
    };

    const handleEventChange = (selectedOption) => {
        setSelectedEvent(selectedOption);
        if (selectedOption) {
            fetchMealData(selectedOption.value);
        } else {
            setMealData([]);
        }
    };

    const handleEdit = () => {
        if (selectedEvent && mealData.length > 0) {
            const eventDetails = mealData[0]?.event_details || {};

            console.log("Session ids: ", eventDetails);
            navigate('/menu_creation/edit', {
                state: {
                    eventId: selectedEvent.value,
                    eventName: selectedEvent.label,
                    maharaj: eventDetails.maharaj,
                    bowlType: eventDetails.bowl_type,
                    from_date: eventDetails.start_date,
                    to_date: eventDetails.end_date,
                    session_ids: eventDetails.session_id
                }
            });
        }

    };

    const handleExportExcel = () => {
        if (!mealData || mealData.length === 0) {
            console.log("No data to export");
            return;
        }
    
        const eventDetails = mealData[0]?.event_details || {};
        const eventName = selectedEvent?.label || "Event";
        const startDate = eventDetails.start_date ? new Date(eventDetails.start_date).toLocaleDateString("en-GB") : "";
        const endDate = eventDetails.end_date ? new Date(eventDetails.end_date).toLocaleDateString("en-GB") : "";
    
        const formattedTitle = `${eventName} General Kitchen Menu \nShibir Dates : ${startDate} - ${endDate}`;
    
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet([]);
    
        // Add title at the top
        XLSX.utils.sheet_add_aoa(worksheet, [[formattedTitle]], { origin: "A1" });
    
        // Dynamically determine sessions
        const allSessions = mealData.length > 0 ? Object.keys(mealData[0].meals) : [];
    
        // Construct table headers dynamically
        const headers = ["Date", ...allSessions.map((session) => session.charAt(0).toUpperCase() + session.slice(1))];
    
        // Add table headers
        XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A3" });
    
        let rowIndex = 4; // Start data from row 4
        const bodyRows = [];   // kept so the column widths can be measured from real content
    
        mealData.forEach((dayData) => {
            const dateObj = new Date(dayData.date);
            //const formattedDate = dateObj.toLocaleDateString("en-GB"); // DD-MM-YYYY format
            const formattedDate = `${dateObj.getDate()}-${dateObj.toLocaleString("en-GB", { month: "short" })}-${dateObj.getFullYear().toString().slice(-2)}`;
            const dayName = dateObj.toLocaleDateString("en-GB", { weekday: "short" }); // Short weekday name (Mon, Tue, etc.)
    
            const fullDate = `${formattedDate}, ${dayName}`; // Example: 18-02-2009, Tue
    
            // Function to format meal items
            const formatMealItems = (items) => {
                if (!items || items.length === 0) return "";
                return items.map((item) => item.item_name).join(", ");
            };
    
            // Generate row dynamically based on session data
            const rowData = [fullDate, ...allSessions.map((session) => formatMealItems(dayData.meals[session]))];
    
            XLSX.utils.sheet_add_aoa(worksheet, [rowData], { origin: `A${rowIndex}` });
            bodyRows.push(rowData);
            rowIndex++;
        });
    
        // What follows is everything the xlsx community build actually writes. Bold, borders,
        // fills, wrap text, frozen panes and page setup are Pro features: the free build accepts
        // a cell's `s` and drops it, so asking for them here would produce a file that looks
        // identical and a reader who thinks it did not work. Verified by unzipping the output --
        // no <pane>, no <pageSetup>, no bold font in styles.xml. Widths, merges, row heights and
        // margins do land, so those are what this tunes.

        // Widths from the longest thing in each column rather than a flat 40. A menu of two items
        // was getting the same column as one of twelve, which on paper is a page of white space
        // beside a truncated cell. Capped so one long day cannot push the sheet off the page,
        // floored so a short column still reads.
        const columnWidth = (index) => {
            const cells = [headers[index], ...bodyRows.map((row) => row[index])];
            const longest = cells.reduce((max, cell) => Math.max(max, String(cell ?? "").length), 0);
            return { wch: Math.min(Math.max(longest + 2, 12), 60) };
        };
        worksheet["!cols"] = headers.map((_, index) => columnWidth(index));

        // Merge title cells across all columns
        worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];

        // Title and header only. Data rows keep their natural height: without wrap text -- which
        // this build cannot write -- a 60pt row holds the same single line as a 15pt one, so the
        // old blanket height bought four times the paper for the same content.
        if (!worksheet["!rows"]) worksheet["!rows"] = [];
        worksheet["!rows"][0] = { hpt: 30 }; // Title
        worksheet["!rows"][2] = { hpt: 20 }; // Header

        // Narrow margins, since the sheet is wide and every cm counts across a menu's columns.
        worksheet["!margins"] = { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 };
    
        // Download the file
        XLSX.utils.book_append_sheet(workbook, worksheet, "Menu Data");
        XLSX.writeFile(workbook, `${eventName}_MenuData.xlsx`);
    };

    const allSessions = mealData.length > 0 
        ? Object.keys(mealData[0].meals) 
        : [];

    const renderMealItems = (items) => {
        if (!items || items.length === 0) return "-";

        return (
            <div className="flex flex-wrap gap-2">
                {items.map((item, index) => (
                    <span
                        key={`${item.id}-${index}`}
                        className="bg-gray-200 px-3 py-1 rounded-2xl text-sm text-center leading-tight"
                    >
                        <ItemNameStacked item={item} />
                    </span>
                ))}
            </div>
        );
    };

    return (
        <div className="p-6">
            {loading && <Loader />}

            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Menu</h1>
                <div className="flex gap-4">
                    {selectedEvent && mealData.length > 0 && (
                        <>
                            <button
                                onClick={handleEdit}
                                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                            >
                                Edit
                            </button>

                            <button
                                onClick={handleExportExcel}
                                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring focus:ring-gray-200"
                            >
                                Export Excel
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="mb-6">
                <Select
                    {...menuAboveTable}
                    className="w-64"
                    value={selectedEvent}
                    onChange={handleEventChange}
                    options={eventList.map(event => ({
                        value: event.id,
                        label: event.event_name
                    }))}
                    placeholder="Select Event"
                    isClearable
                />
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                    {error}
                </div>
            )}
            {/* Meal Data Table */}
            {selectedEvent && mealData.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left border border-gray-300">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-6 py-3 border">Date</th>
                                {allSessions.map((session) => (
                                    <th key={session} className="px-6 py-3 border capitalize">
                                        {session}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {mealData.map((dayData) => (
                                <tr key={dayData.date} className="border-b hover:bg-gray-50">
                                    <td className="px-6 py-4 border font-medium">
                                        {getDateWithDay(dayData.date)}
                                    </td>
                                    {allSessions.map((session) => (
                                        <td key={session} className="px-6 py-4 border">
                                            {renderMealItems(dayData.meals[session] || [])}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedEvent && mealData.length === 0 && (
                <div className="text-center p-8 bg-gray-50 rounded-md">
                    No meal data found for this event.
                </div>
            )}
        </div>
    );
};

export default MenuCreationEdit;